import {
  type BasePaginationResponse,
  type BaseSuccessResponse,
} from "./index.type";

export interface TransactionModel {
  id: string;
  amount: string;
  description: string;
  type: "INCOME" | "EXPENSE";
  reference: string;
  walletId: string | null;
  walletName: string | null;
  programId: string | null;
  programName: string | null;
  // Program status information for program transactions
  program?: {
    statusId?: string;
    statusKey?: string;
    statusName?: string;
  };
  budgetItemId: string | null;
  budgetItemName: string | null;
  budgetItemDisplayNumber: string | null;
  createdById: string;
  createdByName: string;
  createdBy?: {
    role?: {
      name: string;
    };
  };
  createdAt: string;
  updatedAt: string;
  transactionDate: string;
  document?: {
    id: string;
    fileName: string;
    fileKey: string;
    downloadUrl: string;
    documentType: {
      id: string;
      name: string;
      code: string;
    };
  };
}

export namespace GetTransaction {
  export interface Request {
    page?: number;
    limit?: number;
    search?: string;
    type?: "INCOME" | "EXPENSE";
    walletId?: string;
    programId?: string;
    source?: "wallet" | "program";
    createdById?: string;
    startDate?: string;
    endDate?: string;
    sortBy?: string;
    sortOrder?: string;
    document?: {
      id: string;
      fileName: string;
      fileKey: string;
      downloadUrl: string;
      documentType: {
        id: string;
        name: string;
        code: string;
      };
    };
  }

  export interface Response
    extends BasePaginationResponse<TransactionModel[]> {}
}

export namespace CreateTransaction {
  export interface Request {
    amount: string;
    description: string;
    type: "INCOME" | "EXPENSE";
    walletId?: string;
    programId?: string;
    budgetItemId?: string;
    transactionDate?: string;
    fileName?: string;
    fileKey?: string;
  }

  export interface Response extends BaseSuccessResponse<TransactionModel> {}
}

export namespace UpdateTransaction {
  export interface Request {
    transactionId: string;
    amount?: string;
    description?: string;
    type?: "INCOME" | "EXPENSE";
    budgetItemId?: string;
    transactionDate?: string;
    fileName?: string;
    fileKey?: string;
  }

  export interface Response extends BaseSuccessResponse<TransactionModel> {}
}

export namespace DeleteTransaction {
  export interface Request {
    transactionId: string;
  }

  export interface Response extends BaseSuccessResponse<{ message: string }> {}
}

export namespace GetTransactionById {
  export interface Request {
    transactionId: string;
  }

  export interface Response extends BaseSuccessResponse<TransactionModel> {}
}

export namespace GetTransactionSummary {
  export interface ProgramSummaryModel {
    currentBalance: string;
    totalIncome: string;
    totalOutcome: string;
  }

  export interface WalletSummaryModel {
    currentBalance: string;
    totalIncome: string;
    totalOutcome: string;
  }

  export interface Request {
    id?: string;
  }

  export interface Response {
    data: ProgramSummaryModel | WalletSummaryModel;
  }

  export interface ProgramSummaryResponse {
    currentBalance: string;
    totalIncome: string;
    totalOutcome: string;
  }

  export interface WalletSummaryResponse {
    currentBalance: string;
    totalIncome: string;
    totalOutcome: string;
  }
}

export enum IncomeOutcomeType {
  INCOME = "INCOME",
  EXPENSE = "EXPENSE",
}

export namespace GetProgramCashflow {
  export type PeriodType = "day" | "week" | "month";

  export interface CashflowChartData {
    period: string;
    income: number;
    outcome: number;
    balance: number;
  }

  export interface Request {
    programId: string;
    periodType?: PeriodType;
  }

  export interface Response extends BaseSuccessResponse<{
    currentBalance: string;
    totalIncome: string;
    totalOutcome: string;
    chartData: CashflowChartData[];
  }> {}
}

export namespace GetWalletCashflow {
  export type PeriodType = "day" | "week" | "month";

  export interface CashflowChartData {
    period: string;
    income: number;
    outcome: number;
    balance: number;
  }

  export interface Request {
    walletId: string;
    periodType?: PeriodType;
    startDate?: string;
    endDate?: string;
  }

  export interface Response extends BaseSuccessResponse<{
    currentBalance: string;
    totalIncome: string;
    totalOutcome: string;
    chartData: CashflowChartData[];
  }> {}
}
